import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { useResponsive } from '@sg/garnish';

import { Address, useAddressHandlers as useAddressHandlersV2 } from '@order/features/locations';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useCustomer } from '../../context';
import { AddressForm, useAddressHandlers } from '../Address';
import { Modal } from '../Modal';

export const AddressAddModal = (props: AddressAddModalProps) => {
  const { visible, onRequestClose } = props;

  // ─── Flags ────────────────────────────────────────────────────────────

  const isRadarDeliveryEnabled = useFeatureFlag(
    'cels-4823-enable-radar-delivery-address-predictions',
  );

  // ─────────────────────────────────────────────────────────────────────────────

  const { customer } = useCustomer();
  const customerAddresses = customer?.addresses ?? [];

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  const { isAddingAddress, handleOnSave, handleOnEdit, handleOnDelete } = useAddressHandlers({
    checkExistingBag: true,
    onClose: onRequestClose,
  });

  const { handleOnSave: handleOnSaveV2, isAddingAddress: isAddingAddressV2 } = useAddressHandlersV2(
    { onSuccess: onRequestClose },
  );

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Modal
      fitHeight
      visible={visible}
      accessibilityLabel={formatMessage(messages.modalHeadline)}
      presentationStyle="pageSheet"
      size={match(['full', 'small'])}
      onRequestClose={onRequestClose}
    >
      <Modal.Header onClose={onRequestClose}>
        <FormattedMessage {...messages.modalHeadline} />
      </Modal.Header>

      {isRadarDeliveryEnabled ? (
        <Address.Form
          visible={visible}
          customerAddresses={customerAddresses}
          onSubmit={handleOnSaveV2}
          isLoading={isAddingAddressV2}
        />
      ) : (
        <AddressForm
          visible={visible}
          loading={isAddingAddress}
          submitCta={formatMessage(messages.saveAddress)}
          onSave={handleOnSave}
          onEdit={handleOnEdit}
          onDelete={handleOnDelete}
        />
      )}
    </Modal>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────────

const messages = defineMessages({
  saveAddress: {
    defaultMessage: 'Save',
    description: 'Account | Add Address Form | Save',
  },
  modalHeadline: {
    defaultMessage: 'Add Address',
    description: 'Account | Modal header | Add Address',
  },
});

// ─── Types ─────────────────────────────────────────────────────────────

type AddressAddModalProps = Readonly<{
  visible: boolean;
  onRequestClose: () => void;
}>;
